/**
 * Links & portfolio. Rows, not four fixed inputs.
 *
 * `SocialLink` is one row per KIND per author — an invariant enforced by the composite primary key
 * `(author_id, link_kind)`, not a convention — and its own description is explicit that "the
 * website is one of these rows with `linkKind: website` and is deliberately not a property of its
 * own". The prototype draws website, Instagram and video as three separate fields; that shape
 * cannot express the data and would need a fourth field the next time a row is added.
 *
 * EACH ROW CARRIES ITS OWN `isPublic`, separately from the seven per-field switches.
 *
 * THE KIND VOCABULARY IS HAND-COPIED HERE AND THAT IS A FINDING, NOT A CHOICE. `social_link_kind`
 * is a real table whose nine rows an operator may add to — "Adding one is a row, so this is not a
 * closed enum here" — but no operation on any of the five specs serves that vocabulary, so a
 * client has nowhere to read it from. Derived by script over all five specs: zero operations.
 *
 * ★ ITS WORDS FOLLOW WHO IS READING (the owner's live walk of real cabinets, 2026-10-01). For an
 * applicant the links are for the reviewer - "Somewhere we can read something you have already
 * written", "An editor reads your bio and then looks for something you have written". An approved
 * author's profile is read by readers, and those sentences sat on the profiles of authors with a
 * hundred published articles. `applying` false gives the readers' version; omitted, the applicant's.
 */
import { ActionIcon, Button, Group, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { SocialLink } from '../contract';
import { SOCIAL_LINK_KINDS } from '../domain/limits';
import type { FieldErrors } from '../domain/form';
import { visibilityWords } from './Fields';
import { EmptyPanel } from './States';

export interface LinksEditorProps {
  readonly id: string;
  readonly value: readonly SocialLink[];
  readonly onChange: (next: readonly SocialLink[]) => void;
  readonly errors: FieldErrors;
  readonly disabled?: boolean;
  /** Whether this is still an application (`rowsGateSending`). Omitted, it is. */
  readonly applying?: boolean;
}

export function LinksEditor({
  id,
  value,
  onChange,
  errors,
  disabled,
  applying = true,
}: LinksEditorProps): ReactNode {
  const { t } = useTranslation('application');
  // The kind's own name; a code this build does not know is shown as sent.
  const kindName = (code: string): string => {
    const known = SOCIAL_LINK_KINDS.find((kind) => kind.code === code);
    return known === undefined ? code : t(`links.kinds.${known.code}`);
  };
  const usedKinds = new Set(value.map((link) => link.linkKind));
  const firstFreeKind = SOCIAL_LINK_KINDS.find((kind) => !usedKinds.has(kind.code));

  function update(index: number, patch: Partial<SocialLink>): void {
    onChange(value.map((link, i) => (i === index ? { ...link, ...patch } : link)));
  }

  const addButton = (
    <Button
      size="xs"
      variant="light"
      disabled={disabled === true || firstFreeKind === undefined}
      onClick={() => {
        if (firstFreeKind === undefined) return;
        onChange([...value, { linkKind: firstFreeKind.code, url: '', isPublic: true }]);
      }}
    >
      {t('links.add')}
    </Button>
  );

  return (
    <Stack gap="sm" id={id}>
      <Stack gap={2}>
        <Text fw={700} size="sm">
          {t('links.title')}
        </Text>
        <Text size="xs" c="dimmed">
          {applying ? t('links.hint.applying') : t('links.hint.author')}
        </Text>
      </Stack>

      {value.length === 0 ? (
        <EmptyPanel
          title={t('links.empty.title')}
          description={applying ? t('links.empty.applying') : t('links.empty.author')}
          action={addButton}
        />
      ) : (
        <Stack gap="md">
          {value.map((link, index) => {
            const kindError = errors[`links.${index}.linkKind`];
            const urlError = errors[`links.${index}.url`];
            return (
              <Stack key={`${link.linkKind}-${index}`} gap={6}>
                <Group align="flex-start" gap="sm" wrap="nowrap">
                  <Select
                    aria-label={t('links.kindLabel')}
                    data={SOCIAL_LINK_KINDS.map((kind) => ({
                      value: kind.code,
                      label: t(`links.kinds.${kind.code}`),
                      // One per kind. The already-used ones stay visible but cannot be picked, so
                      // the person can see why the choice is not there.
                      disabled: kind.code !== link.linkKind && usedKinds.has(kind.code),
                    }))}
                    value={link.linkKind}
                    disabled={disabled === true}
                    error={kindError ?? null}
                    w={150}
                    onChange={(next) => {
                      if (next !== null) update(index, { linkKind: next });
                    }}
                  />
                  <TextInput
                    aria-label={t('links.urlLabel')}
                    placeholder="https://"
                    style={{ flex: 1 }}
                    value={link.url}
                    disabled={disabled === true}
                    error={urlError ?? null}
                    onChange={(event) => {
                      update(index, { url: event.currentTarget.value });
                    }}
                  />
                  <ActionIcon
                    variant="subtle"
                    aria-label={t('links.remove', {
                      kind: kindName(link.linkKind),
                    })}
                    disabled={disabled === true}
                    onClick={() => {
                      onChange(value.filter((_unused, i) => i !== index));
                    }}
                  >
                    ×
                  </ActionIcon>
                </Group>
                <Switch
                  size="xs"
                  checked={link.isPublic}
                  disabled={disabled === true}
                  // The same short words as every switch on the page; the link's kind in its name.
                  aria-label={
                    link.isPublic
                      ? t('links.visibility.onNamed', { kind: kindName(link.linkKind) })
                      : t('links.visibility.offNamed', { kind: kindName(link.linkKind) })
                  }
                  onChange={(event) => {
                    update(index, { isPublic: event.currentTarget.checked });
                  }}
                  label={
                    <Text size="xs" c="dimmed">
                      {visibilityWords(link.isPublic, t)}
                    </Text>
                  }
                />
              </Stack>
            );
          })}
          <Group>
            {addButton}
            {firstFreeKind === undefined ? (
              <Text size="xs" c="dimmed">
                {t('links.allKinds')}
              </Text>
            ) : null}
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
